import { defineStore } from 'pinia'

import { StoreState } from '@shared/models/interfaces/store'
import type { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { INITIAL_PAGINATION } from '@shared/utils/constants/pagination'
import { getDataToSet } from '@shared/utils/store/data-to-set'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { AssignedConversation } from '@protos/apps/vc/assigned_conversation_pb'
import { NewAssignmentsCount } from '@protos/apps/vc/response/badge/new_assignments_count_pb'
import { UnreadMessagesCount } from '@protos/apps/vc/response/badge/unread_messages_count_pb'
import { UnreadMessage } from '@protos/apps/vc/unread_message_pb'

import { GetNewAssignmentsDTO } from '@vc/models/proto/dto/conversations'
import { GetUnreadMessagesDTO } from '@vc/models/proto/dto/messages'
import { NewAssignmentsRO } from '@vc/models/proto/ro/conversations'
import { MessageIndexResponse } from '@vc/models/proto/ro/messages'

export interface State {
  unreadMessages: StoreState<MessageIndexResponse>
  unreadMessagesCount: UnreadMessagesCount['data']
  newAssignments: StoreState<NewAssignmentsRO>
  newAssignmentsCount: NewAssignmentsCount['data']
  isShowClearAllMessage: boolean
  isShowClearAllAssignments: boolean
  lastUnreadMessageId: Nullable<number>
}

const state = (): State => ({
  unreadMessages: getInitialState(),
  unreadMessagesCount: 0,
  newAssignments: getInitialState(),
  newAssignmentsCount: 0,
  isShowClearAllMessage: false,
  isShowClearAllAssignments: false,
  lastUnreadMessageId: null,
})

export const apiTransport = new ApiTransport(resourcePath, 'vc')

export const useUnreadCountsStore = defineStore('unreadCounts', {
  state,

  actions: {
    async fetchUnreadMessages(dto?: { page?: number; perPage?: number }) {
      const baseDto = {
        page: 1,
        perPage: 15,
      }
      this.toggleClearMessagesButton(false)

      try {
        this.unreadMessages = updateState(this.unreadMessages)

        const { meta, data = [] } = (await apiTransport.get<
          GetUnreadMessagesDTO,
          MessageIndexResponse
        >(
          '/messages/get_unread_messages',
          new GetUnreadMessagesDTO(Object.assign(baseDto, dto)),
          MessageIndexResponse
        )) ?? { meta: {}, data: [] }

        const { data: existingList = [] } = this.unreadMessages?.data ?? {}

        const updatedData = getDataToSet<UnreadMessage>(
          dto?.page ?? 1,
          data,
          (existingList ?? []) as UnreadMessage[]
        )

        this.unreadMessages = updateState(
          this.unreadMessages,
          new MessageIndexResponse({
            data: updatedData || [],
            meta,
          })
        )

        if (meta?.totalCount) {
          this.unreadMessagesCount = meta.totalCount
        }

        if (updatedData?.length > 0) {
          this.toggleClearMessagesButton(true)
        }
      } catch (e) {
        this.unreadMessages = updateState(
          this.unreadMessages,
          new Error(`${e}`)
        )
        throw e
      }
    },
    async fetchNewAssignments(dto?: { page?: number; perPage?: number }) {
      const baseDto = {
        page: 1,
        perPage: 15,
      }
      this.toggleClearAssignmentsButton(false)
      this.newAssignments = updateState(this.newAssignments)
      try {
        const response = await apiTransport.get<
          GetNewAssignmentsDTO,
          NewAssignmentsRO
        >(
          '/conversations/get_new_assignments',
          new GetNewAssignmentsDTO(Object.assign(baseDto, dto)),
          NewAssignmentsRO
        )

        const { data: existingList = [] } = this.newAssignments?.data ?? {}

        const updatedData = getDataToSet<AssignedConversation>(
          dto?.page ?? 1,
          response?.data || [],
          existingList as AssignedConversation[]
        )

        this.newAssignments = updateState(
          this.newAssignments,
          new NewAssignmentsRO({
            data: updatedData,
            meta: response?.meta,
          })
        )

        if (updatedData?.length > 0) {
          this.toggleClearAssignmentsButton(true)
        }
      } catch (e) {
        this.newAssignments = updateState(
          this.newAssignments,
          new Error(`${e}`)
        )
        throw e
      }
    },

    async fetchUnreadMessagesCount() {
      const response =
        await apiTransport.getWithoutRequest<UnreadMessagesCount>(
          '/badges/get_unread_messages_count',
          UnreadMessagesCount
        )
      this.unreadMessagesCount = response?.data || 0
    },

    async fetchNewAssignmentsCount() {
      const response =
        await apiTransport.getWithoutRequest<NewAssignmentsCount>(
          '/badges/get_new_assignments_count',
          NewAssignmentsCount
        )
      this.newAssignmentsCount = response?.data || 0
    },

    async clearAssignments() {
      await apiTransport.putWithoutRequest(
        '/conversations/mark_assignments_seen'
      )
      this.newAssignmentsCount = 0
      this.newAssignments = getInitialState()
    },

    async clearMessages() {
      await apiTransport.putWithoutRequest('/messages/mark_unread_seen')
      this.unreadMessagesCount = 0
      this.unreadMessages = getInitialState()
    },

    decreaseUnreadMessagesCount(messageId?: Nullable<number>) {
      if (messageId) {
        this.lastUnreadMessageId = messageId
      }
      if (this.unreadMessagesCount >= 1) {
        this.unreadMessagesCount -= 1
      }
    },

    increaseUnreadMessagesCount(messageId?: Nullable<number>) {
      if (messageId) {
        const delay = 2000 // Delay scrolling the message list to the bottom to ensure messages are marked as read.
        // If the current conversation contains a message, updating unread messages should be delayed to ensure the message is marked as read first (it calls decreaseUnreadMessagesCount).
        setTimeout(() => {
          if (messageId === this.lastUnreadMessageId) {
            return
          }
          this.unreadMessagesCount += 1
          this.isShowClearAllMessage = true
        }, delay)

        return
      }

      this.unreadMessagesCount += 1
      this.isShowClearAllMessage = true
    },

    decreaseAssignmentsCount() {
      if (this.newAssignmentsCount >= 1) {
        this.newAssignmentsCount -= 1
      }
    },

    increaseAssignmentsCount() {
      this.newAssignmentsCount += 1
      this.isShowClearAllAssignments = true
    },

    toggleClearAssignmentsButton(val: boolean) {
      this.isShowClearAllAssignments = val
    },

    toggleClearMessagesButton(val: boolean) {
      this.isShowClearAllMessage = val
    },
  },
  getters: {
    unreadMessagesData: (state) => state.unreadMessages.data?.data ?? [],
    unreadMessagesMeta: (state) =>
      state.unreadMessages.data?.meta ?? INITIAL_PAGINATION,

    newAssignmentsData: (state) => state.newAssignments.data?.data ?? [],
    newAssignmentsMeta: (state) =>
      state.newAssignments.data?.meta ?? INITIAL_PAGINATION,
  },
})
